import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"

type PageEntry = {
	chord: string
	description: string
	name: string
	path: string
}

const PAGES: PageEntry[] = [
	{
		chord: "g h",
		description: "Overview and getting started",
		name: "Home",
		path: "/",
	},
	{
		chord: "g l",
		description: "View all 28 color spaces",
		name: "Color Spaces",
		path: "/color-spaces",
	},
	{
		chord: "g s",
		description: "Named color standards",
		name: "Standards",
		path: "/standards",
	},
	{
		chord: "g c",
		description: "Convert between color spaces",
		name: "Convert",
		path: "/convert",
	},
	{
		chord: "g p",
		description: "Generate shade palettes",
		name: "Palettes",
		path: "/palettes",
	},
	{
		chord: "g w",
		description: "Color harmony wheel",
		name: "Harmony",
		path: "/harmony",
	},
	{
		chord: "g i",
		description: "Interpolation and gradients",
		name: "Interpolation",
		path: "/interpolation",
	},
	{
		chord: "g d",
		description: "Data visualization palettes",
		name: "Data Viz",
		path: "/data-viz",
	},
	{
		chord: "g j",
		description: "Lightness, saturation, tints",
		name: "Adjustments",
		path: "/adjustments",
	},
	{
		chord: "g b",
		description: "W3C and Porter-Duff blending",
		name: "Blend Modes",
		path: "/blend-modes",
	},
	{
		chord: "g e",
		description: "Analog video effects and tone mapping",
		name: "Analog Effects",
		path: "/analog-effects",
	},
	{
		chord: "g a",
		description: "WCAG contrast and APCA",
		name: "Accessibility",
		path: "/accessibility",
	},
	{
		chord: "g v",
		description: "Color vision deficiency simulation",
		name: "Color Blindness",
		path: "/color-blindness",
	},
	{
		chord: "g f",
		description: "DeltaE color difference metrics",
		name: "Color Diff",
		path: "/color-diff",
	},
	{
		chord: "g x",
		description: "Extract colors from images",
		name: "Image Extraction",
		path: "/image-extraction",
	},
	{
		chord: "g m",
		description: "AlphaMixer modes for transparent color blending",
		name: "Alpha Mixing",
		path: "/alpha-mixing",
	},
	{
		chord: "g y",
		description: "Parse and convert any CSS Color Level 4 string",
		name: "CSS Playground",
		path: "/css-playground",
	},
	{
		chord: "g u",
		description: "Clip vs map wide-gamut colors into sRGB",
		name: "Gamut Mapping",
		path: "/gamut-mapping",
	},
	{
		chord: "g o",
		description: "HDR → SDR pipeline: white balance, tone mapping",
		name: "Color Correction",
		path: "/color-correction",
	},
	{
		chord: "g ?",
		description: "Keyboard shortcuts reference",
		name: "Help",
		path: "/help",
	},
]

export function CommandPalette() {
	const [open, setOpen] = useState(false)
	const [query, setQuery] = useState("")
	const [selectedIndex, setSelectedIndex] = useState(0)
	const inputRef = useRef<HTMLInputElement>(null)
	const navigate = useNavigate()

	useEffect(() => {
		function handleKeyDown(e: KeyboardEvent) {
			if ((e.ctrlKey || e.metaKey) && e.key === "k") {
				e.preventDefault()
				setOpen((prev) => !prev)
			}
		}
		window.addEventListener("keydown", handleKeyDown)
		return () => window.removeEventListener("keydown", handleKeyDown)
	}, [])

	useEffect(() => {
		if (open) {
			setQuery("")
			setSelectedIndex(0)
			requestAnimationFrame(() => inputRef.current?.focus())
		}
	}, [open])

	const filtered = query
		? PAGES.filter((p) => {
				const q = query.toLowerCase()
				return (
					p.name.toLowerCase().includes(q) ||
					p.description.toLowerCase().includes(q)
				)
			})
		: PAGES

	const go = useCallback(
		(path: string) => {
			setOpen(false)
			navigate(path)
		},
		[navigate],
	)

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "ArrowDown") {
				e.preventDefault()
				setSelectedIndex((i) => (i + 1) % filtered.length)
			} else if (e.key === "ArrowUp") {
				e.preventDefault()
				setSelectedIndex((i) => (i - 1 + filtered.length) % filtered.length)
			} else if (e.key === "Enter") {
				e.preventDefault()
				const item = filtered[selectedIndex]
				if (item) go(item.path)
			} else if (e.key === "Escape") {
				e.preventDefault()
				setOpen(false)
			}
		},
		[filtered, selectedIndex, go],
	)

	if (!open) return null

	return (
		<div className="fixed inset-0 z-[200] flex items-start justify-center pt-[15vh]">
			<button
				aria-label="Close command palette"
				className="absolute inset-0 bg-black/60 backdrop-blur-sm"
				onClick={() => setOpen(false)}
				type="button"
			/>
			<div className="relative w-full max-w-lg bg-neutral-900 border border-neutral-700 rounded-2xl shadow-2xl overflow-hidden">
				<input
					className="w-full px-4 py-3 bg-transparent text-neutral-200 placeholder-neutral-500 text-sm outline-none border-b border-neutral-800"
					onChange={(e) => {
						setQuery(e.target.value)
						setSelectedIndex(0)
					}}
					onKeyDown={handleKeyDown}
					placeholder="Go to page..."
					ref={inputRef}
					type="text"
					value={query}
				/>
				<div className="max-h-80 overflow-y-auto py-1">
					{filtered.length === 0 && (
						<div className="px-4 py-6 text-center text-sm text-neutral-500">
							No results
						</div>
					)}
					{filtered.map((page, i) => (
						<button
							className={`w-full text-left px-4 py-2.5 flex items-center justify-between gap-3 text-sm transition-colors ${
								i === selectedIndex
									? "bg-neutral-800 text-neutral-200"
									: "text-neutral-400 hover:bg-neutral-800/50"
							}`}
							key={page.path}
							onClick={() => go(page.path)}
							onMouseEnter={() => setSelectedIndex(i)}
							type="button"
						>
							<div className="min-w-0">
								<div className="font-medium truncate">{page.name}</div>
								<div className="text-xs text-neutral-500 truncate">
									{page.description}
								</div>
							</div>
							<kbd className="shrink-0 px-1.5 py-0.5 rounded bg-neutral-800 text-neutral-500 font-mono text-[10px] border border-neutral-700">
								{page.chord}
							</kbd>
						</button>
					))}
				</div>
			</div>
		</div>
	)
}
